import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { api, type ProductQuestion } from "@/api";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { Skeleton } from "@osmix/ui/skeleton";
import { useProfile } from "@/store";
const formatDate = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", {
  day: "numeric",
  month: "long",
  year: "numeric"
});

/** Ключ запроса вопросов товара: один на счётчик в карточке и вкладку панели. */
export const productQuestionsKey = (productId: string) => ["products", "questions", productId] as const;

/** Вопросы товара (счётчик «Вопросы (N)» у рейтинга и вкладка панели). */
export const useProductQuestions = (productId, t0) => {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== productId) {
    t1 = productQuestionsKey(productId);
    $[0] = productId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== productId) {
    t2 = () => api.products.questions(productId);
    $[2] = productId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== enabled || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled,
      staleTime: 60000
    };
    $[4] = enabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};

/*
 * Вкладка «Вопросы» панели «Отзывы и вопросы» (fd-review-02/03).
 *
 * Покупатель задаёт вопрос о товаре - он уходит магазину уведомлением, ответ
 * магазина появляется здесь же под вопросом (подпись - название магазина).
 * Задать вопрос может только вошедший покупатель: вопрос без аккаунта не к
 * кому вернуть ответом. Фильтр «С ответом» - по аналогии с «только с фото» у
 * отзывов. Вид карточек - как у отзывов: белая карточка r16 на #F8F8F8.
 */
export function ProductQuestionsPane({
  productId
}: {
  productId: string;
}) {
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = useProfile(s => s.token);
  const questions = useProductQuestions(productId);
  const [asking, setAsking] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [answeredOnly, setAnsweredOnly] = useState(false);
  const items = questions.data?.items ?? [];
  const visible = answeredOnly ? items.filter(q => q.answer) : items;
  const startAsking = () => {
    if (!token) {
      toast(t("product.qa.signInToAsk"), {
        action: {
          label: t("product.qa.signIn"),
          onClick: () => navigate("/profile")
        }
      });
      return;
    }
    setAsking(true);
  };
  const submit = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      const created = await api.buyer.askQuestion(productId, body);
      queryClient.setQueryData(productQuestionsKey(productId), (prev: {
        productId: string;
        items: ProductQuestion[];
      } | undefined) => prev ? {
        ...prev,
        items: [created, ...prev.items.filter(q_0 => q_0.id !== created.id)]
      } : {
        productId,
        items: [created]
      });
      setDraft("");
      setAsking(false);
      toast.success(t("product.qa.sent"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  return <div className="flex flex-col" data-testid="product-questions">
			<div className="py-2.5 pr-5 pl-4 sm:pl-[30px]">
				<div className="flex flex-col gap-3 rounded-[16px] bg-white px-3 py-4">
					{asking ? <>
							<label htmlFor="product-question-body" className="text-[16px] leading-[21px] font-medium text-ds-primary">
								{t("product.qa.askTitle")}
							</label>
							<textarea id="product-question-body" autoFocus value={draft} onChange={e_0 => setDraft(e_0.target.value.slice(0, 2000))} placeholder={t("product.qa.placeholder")} rows={4} data-testid="question-input" className="w-full resize-y rounded-[12px] bg-[#F8F8F8] px-3 py-2.5 text-[14px] leading-[18px] text-black outline-none placeholder:text-black/40 focus-visible:ring-2 focus-visible:ring-ds-focus" />
							<p className="text-[12px] leading-4 text-black/50">{t("product.qa.hint")}</p>
							<div className="flex flex-wrap gap-2">
								<Button onClick={() => void submit()} disabled={busy || !draft.trim()} data-testid="question-submit">
									{busy ? t("product.qa.sending") : t("product.qa.send")}
								</Button>
								<Button variant="ghost" onClick={() => setAsking(false)} disabled={busy}>
									{t("product.qa.cancel")}
								</Button>
							</div>
						</> : <div className="flex flex-wrap items-center justify-between gap-3">
							<p className="min-w-0 flex-1 text-[14px] leading-[18px] text-black/60">{t("product.qa.lead")}</p>
							<Button onClick={startAsking} data-testid="ask-question">
								{t("product.qa.ask")}
							</Button>
						</div>}
				</div>
			</div>

			{items.length > 0 && <div className="flex flex-wrap gap-2.5 py-2.5 pr-5 pl-4 sm:pl-[30px]">
					<Chip selected={!answeredOnly} onClick={() => setAnsweredOnly(false)}>
						{t("product.qa.all", {
          count: items.length
        })}
					</Chip>
					<Chip selected={answeredOnly} onClick={() => setAnsweredOnly(true)}>
						{t("product.qa.answered")}
					</Chip>
				</div>}

			{questions.isPending ? <div className="flex flex-col gap-2.5 px-5 py-2.5 sm:pl-[30px]">
					<Skeleton className="h-[120px] rounded-[16px] bg-white" />
				</div> : questions.isError ? <p className="px-5 py-2.5 text-[14px] text-black/50 sm:pl-[30px]">{t("product.qa.loadError")}</p> : items.length === 0 ? <p className="px-5 py-2.5 text-[14px] text-black/50 sm:pl-[30px]">{t("product.qa.empty")}</p> : visible.length === 0 ? <p className="px-5 py-2.5 text-[14px] text-black/50 sm:pl-[30px]">{t("product.qa.noAnswered")}</p> : <ul className="flex flex-col">
					{visible.map(question => <li key={question.id} className="py-2.5 pr-5 pl-4 sm:pl-[30px]">
							<article className="flex flex-col gap-3 rounded-[16px] bg-white p-2.5" data-testid="product-question">
								<p className="flex flex-wrap items-baseline gap-x-2">
									<span className="text-[14px] leading-[18px] font-medium text-[#1F1F1F]">{question.authorName}</span>
									<span className="text-[12px] leading-[18px] text-black/50">{formatDate(question.createdAt)}</span>
								</p>
								<p className="text-[14px] leading-[18px] whitespace-pre-line break-words text-black">{question.body}</p>
								{question.answer ? <div className="flex flex-col gap-1 pl-4" data-testid="question-answer">
										<span className="text-[14px] leading-[18px] text-black/50">
											{t("product.qa.answerFrom", {
                shop: question.answer.authorName
              })}
										</span>
										<p className="text-[14px] leading-[18px] whitespace-pre-line break-words text-black">{question.answer.body}</p>
									</div> : <p className="pl-4 text-[14px] leading-[18px] text-black/50">{t("product.qa.waiting")}</p>}
							</article>
						</li>)}
				</ul>}
		</div>;
}